import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { authApiSlice } from '../../services';
import { UserDetails } from '../../models';

export interface AuthState {
  accessToken: null | string;
  refreshToken: null | string;
  userDetails: null | UserDetails;
}

// Initialize accessToken from local storage
const accessToken = localStorage.getItem('accessToken') || null;
const refreshToken = localStorage.getItem('refreshToken') || null;
// const userDetails = localStorage.getItem('userDetails')
//   ? (JSON.parse(localStorage.getItem('userDetails') as string) as UserDetails)
//   : null;

const initialState: AuthState = {
  accessToken,
  refreshToken,
  userDetails: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      { payload }: PayloadAction<Omit<AuthState, 'userDetails'>>
    ) => {
      state.accessToken = payload.accessToken;
      state.refreshToken = payload.refreshToken;
    },
    logout: state => {
      state.accessToken = null;
      state.refreshToken = null;
    },
  },
  extraReducers: builder => {
    // login is the ONLY endpoint allowed to establish a session: it is the only
    // one that authenticates a password, and the only one whose payload carries
    // an access/refresh pair.
    builder.addMatcher(
      authApiSlice.endpoints.login.matchFulfilled,
      (state, { payload }) => {
        state.accessToken = payload.data.accessToken;
        state.refreshToken = payload.data.refreshToken;
      }
    );

    // There is deliberately NO matcher on verifyAccount.matchFulfilled here.
    // Since sprint 2 that endpoint returns { userName, resetToken } and issues
    // no tokens at all, so copying its payload into this slice did two bad
    // things: it wrote undefined over the live accessToken/refreshToken of any
    // already-signed-in user who ran forgot-password (Root.tsx picks its route
    // set from those two keys and api-slice builds the Authorization header
    // from accessToken, so they were silently logged out), and it granted a
    // session to someone who had only proved possession of an OTP — the
    // takeover path sprint 2 closed. Verifying an OTP proves nothing but that
    // the code was received; the reset flow ends at /login, where a password is
    // actually checked.

    builder.addMatcher(
      authApiSlice.endpoints.getUserDetails.matchFulfilled,
      (state, { payload }) => {
        state.userDetails = payload.data;
      }
    );
  },
});

export const { logout, setCredentials } = authSlice.actions;
export const authReducer = authSlice.reducer;
